import React, { useEffect, useRef, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import type { StationDetailOutletContext } from "./KardaStationDetailPage";
import { isAxiosError } from "axios";
import {
  StationForm,
  StationFormData,
  EMPTY_FORM,
  validateForm,
} from "./StationForm";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useKardaAdmin } from "./KardaAdminContext";
import type { StationDetail } from "./KardaAdminContext";

type FormErrors = Partial<Record<keyof StationFormData, string>>;

function stationToForm(station: StationDetail): StationFormData {
  const m = station.metadata ?? {};
  return {
    name: station.name ?? "",
    city: String(m.city ?? ""),
    state: String(m.state ?? ""),
    zip_code: String(m.zip_code ?? ""),
    county: String(m.county ?? ""),
    address: String(m.address ?? ""),
    contact_email: String(m.contact_email ?? ""),
    contact_number: String(m.contact_number ?? ""),
    rms_used: String(m.rms_used ?? ""),
    bodycam_vendor: String(m.bodycam_vendor ?? ""),
    seat_limit: String(m.seat_limit ?? ""),
    subscription_tier: String(m.subscription_tier ?? ""),
    service_area_plus_code: String(m.service_area_plus_code ?? ""),
  };
}

const StationDetailsSection: React.FC = () => {
  const { stationId } = useParams<{ stationId: string }>();
  const { setHeaderAction } = useOutletContext<StationDetailOutletContext>();
  const { stationDetails, updateStation } = useKardaAdmin();
  const station = stationDetails[stationId!] ?? null;

  const {
    showError,
    showSuccess,
    snackBarMessage,
    snackBarMode,
    handleCloseSnackbar,
  } = useErrorSnackbar();

  const [formData, setFormData] = useState<StationFormData>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [readonly, setReadonly] = useState(true);

  const previousStationIdRef = useRef<string | undefined>(undefined);

  // Rehydrate form only on initial load, station switch, or when in readonly mode.
  // In edit mode (readonly=false) cache refreshes must not overwrite in-progress edits.
  useEffect(() => {
    if (!station) return;
    const isNewStation = previousStationIdRef.current !== station.id;
    previousStationIdRef.current = station.id;
    if (isNewStation || readonly) {
      setFormData(stationToForm(station));
    }
  }, [station, readonly]);

  const handleFieldChange = (field: keyof StationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Push edit/save/cancel actions into the parent header
  useEffect(() => {
    if (readonly) {
      setHeaderAction(
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setReadonly(false)}
          size="small"
        >
          Edit Station
        </Button>,
      );
    } else {
      setHeaderAction(
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={() => {
              setReadonly(true);
              if (station) setFormData(stationToForm(station));
              setFormErrors({});
            }}
            disabled={isSaving}
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => {
              handleSaveRef.current();
            }}
            disabled={isSaving}
            size="small"
          >
            Save Changes
          </Button>
        </Box>,
      );
    }
    return () => setHeaderAction(null);
  }, [readonly, isSaving, station, setHeaderAction]);

  const handleSaveRef = useRef(() => {});

  const handleSave = async () => {
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSaving(true);
    try {
      await updateStation(stationId!, formData);
      showSuccess("Station updated successfully.");
      setReadonly(true);
    } catch (err) {
      if (isAxiosError(err)) {
        showError(err.response?.data?.message || err.message);
      } else {
        showError("Failed to update station.");
      }
    } finally {
      setIsSaving(false);
    }
  };
  handleSaveRef.current = handleSave;

  return (
    <>
      <Typography variant="h6" fontWeight="bold" marginBottom={1}>
        Edit Station Details
      </Typography>
      <StationForm
        formData={formData}
        formErrors={formErrors}
        handleFieldChange={handleFieldChange}
        disabled={isSaving || readonly}
      />
      <SnackBarMessage
        message={snackBarMessage}
        mode={snackBarMode}
        handleClose={handleCloseSnackbar}
      />
    </>
  );
};

export default StationDetailsSection;
